/**
 * Grocery Category Detail Page - Refactored
 * Uses modular architecture with services and components
 */

import { CategoryService } from '../services/CategoryService.js';
import { ProductService } from '../services/ProductService.js';
import { FilterService } from '../services/FilterService.js';
import { renderProductCard } from '../components/ProductCard.js';
import { ScrollableRail } from '../components/ScrollableRail.js';
import { SkeletonLoader } from '../components/SkeletonLoader.js';
import { FilterPanel } from '../components/FilterPanel.js';
import { initCurrencyToggle } from '../components/CurrencyToggle.js';
import { escapeHtml } from '../../utils/validation.js';
import currencyManager from '../../utils/currency.js';
import { t } from '../utils/i18n.js';
import { buildMediaUrl } from '../utils/media.js';
import type { Category, Product, FilterParams, Store } from '../../types/index.js';

// Page state
let currentCategorySlug: string | null = null;
let currentSubcategorySlug: string | null = null;
let currentCategory: Category | null = null;
let filterPanel: FilterPanel | null = null;
let mobileFilterPanel: FilterPanel | null = null;

// Infinite scroll state
const ITEMS_PER_PAGE = 30;
let currentOffset = 0;
let isLoadingMore = false;
let hasMoreProducts = true;
let currentFilters: FilterParams = {};

interface ProductsApiResponse {
    success: boolean;
    data?: {
        products?: Product[];
        stores?: Record<string, Store>;
        filters?: any;
        currency?: any;
        has_more?: boolean;
        total_count?: number;
        pagination?: {
            has_next?: boolean;
            has_more?: boolean;
            total_items?: number;
        };
    };
}

function removeInfiniteScrollLoading(): void {
    document.querySelectorAll('.infinite-scroll-loading').forEach(el => el.remove());
}

function renderCategoryHeader(totalCount?: number): void {
    const titleEl = document.getElementById('category-title');
    const descriptionEl = document.getElementById('category-description');
    const metaEl = document.getElementById('category-meta');
    const breadcrumbEl = document.getElementById('category-breadcrumb');
    if (!currentCategory) return;

    const categoryName = (currentCategory as any).translated_name || currentCategory.name;
    const activeSubcategory = currentSubcategorySlug
        ? (currentCategory.children || []).find(child => child.slug === currentSubcategorySlug)
        : null;
    const activeName = activeSubcategory
        ? ((activeSubcategory as any).translated_name || activeSubcategory.name)
        : categoryName;

    if (titleEl) titleEl.textContent = activeName;
    if (descriptionEl) {
        descriptionEl.textContent = t('Compare current grocery offers, package sizes, and unit prices in this category.');
    }
    if (metaEl && typeof totalCount === 'number') {
        metaEl.textContent = `${totalCount} ${t(totalCount === 1 ? 'item' : 'items')}`;
        metaEl.classList.remove('hidden');
    }

    if (breadcrumbEl) {
        const parts = activeSubcategory && activeName !== categoryName
            ? [categoryName, activeName]
            : [categoryName];
        breadcrumbEl.innerHTML = parts.map(part => `<span>${escapeHtml(part)}</span>`).join(
            '<span class="text-slate-300">/</span>'
        );
    }
}

/**
 * Render subcategories
 */
function renderSubcategories(subcategories: Category[] | null | undefined, activeSlug: string | null = null): void {
    const grid = document.getElementById('subcategories-grid');
    if (!grid) return;

    if (!subcategories || subcategories.length === 0) {
        grid.innerHTML = '';
        grid.closest('.subcategories-section')?.classList.add('hidden');
        return;
    }

    grid.closest('.subcategories-section')?.classList.remove('hidden');

    const html = subcategories.map(subcategory => {
        const name = (subcategory as any).translated_name || subcategory.name;
        const slug = subcategory.slug;
        const isActive = slug === activeSlug;
        const activeClasses = isActive 
            ? 'bg-brand-primary text-white border-brand-primary' 
            : 'bg-white text-slate-700 border-gray-300 hover:bg-gray-50';

        return `
            <button 
                type="button"
                class="subcategory-chip flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-200 whitespace-nowrap ${activeClasses}"
                data-slug="${escapeHtml(slug)}"
                ${isActive ? 'aria-pressed="true"' : 'aria-pressed="false"'}
            >
                ${escapeHtml(name)}
            </button>
        `;
    }).join('');

    grid.innerHTML = html;

    // Initialize scrollable rail
    const railId = grid.id;
    if (railId) {
        const rail = new ScrollableRail(railId);
        rail.init();
        
        // Handle alignment (center if fits, start if overflows)
        updateRailAlignment(grid);
        window.addEventListener('resize', () => updateRailAlignment(grid));
    }
}

/**
 * Update rail alignment based on overflow
 */
function updateRailAlignment(rail: HTMLElement | null): void {
    if (!rail) return;
    
    // Check if content overflows
    const isOverflowing = rail.scrollWidth > rail.clientWidth;
    
    if (isOverflowing) {
        rail.classList.remove('justify-center');
    } else {
        rail.classList.add('justify-center');
    }
}

/**
 * Render products grid
 */
function renderProducts(products: Product[] | null | undefined, storesLookup: Record<string, Store> = {}): void {
    const grid = document.getElementById('products-grid');
    if (!grid) return;

    if (!products || products.length === 0) {
        grid.innerHTML = `
            <div class="col-span-full text-center py-12">
                <p class="text-slate-500">${escapeHtml(t('No products found'))}</p>
            </div>
        `;
        return;
    }

    // Process products for display with stores lookup
    const productItems = ProductService.processProductsForDisplay(products, storesLookup);
    
    // Render product cards
    const html = productItems.map(item => renderProductCard(item, storesLookup)).join('');
    grid.innerHTML = html;
}

/**
 * Fetch and render products (with infinite scroll support)
 */
async function fetchAndRenderProducts(
    categorySlug: string, 
    filters: FilterParams = {}, 
    append: boolean = false
): Promise<void> {
    const grid = document.getElementById('products-grid');
    if (!grid) return;

    // If not appending, reset state and show loading
    if (!append) {
        currentOffset = 0;
        hasMoreProducts = true;
        currentFilters = { ...filters };
        removeInfiniteScrollLoading();
        
        // Show loading state on filter panel
        const filterPanelEl = document.getElementById('filter-panel');
        const filterSidebar = document.getElementById('filterSidebar');
        if (filterPanelEl) filterPanelEl.classList.add('opacity-50', 'pointer-events-none');
        if (filterSidebar) filterSidebar.classList.add('opacity-50', 'pointer-events-none');
        
        const loader = new SkeletonLoader();
        grid.innerHTML = loader.renderProductCardSkeleton(12);
    }

    try {
        const lang = document.documentElement.lang?.split('-')[0] || 'en';
        const baseUrl = `/grocery/api/products/`;
        
        const params: Record<string, any> = {
            ...currentFilters,
            category_slug: categorySlug,
            currency: currencyManager.getCode(),
            language: lang,
            limit: ITEMS_PER_PAGE,
            offset: currentOffset
        };

        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => searchParams.append(key, String(v)));
            } else if (value !== undefined && value !== null && value !== '') {
                searchParams.append(key, String(value));
            }
        });

        const response = await fetch(baseUrl + '?' + searchParams.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const json = await response.json() as ProductsApiResponse;
        if (json.success && json.data) {
            const products = json.data.products || [];
            const storesLookup = json.data.stores || {};
            
            // Apply currency info from API response (symbol, position)
            if (json.data.currency) {
                currencyManager.applyCurrencyInfo(json.data.currency);
            }
            
            if (append) {
                // Append products to existing grid
                appendProducts(products, storesLookup);
            } else {
                // Replace grid content
                renderProducts(products, storesLookup);
            }
            
            // Update filter facets (only on initial load)
            if (!append && json.data.filters) {
                console.log('[Filters] Received filters data:', json.data.filters);
                if (filterPanel) filterPanel.updateFacets(json.data.filters);
                if (mobileFilterPanel) mobileFilterPanel.updateFacets(json.data.filters);
            }
            
            // Update load more state - API uses has_more, not has_next
            if (json.data.has_more !== undefined) {
                hasMoreProducts = json.data.has_more;
                console.log('[InfiniteScroll] has_more:', hasMoreProducts, 'total_count:', json.data.total_count);
                
                // Update total count
                const totalCountEl = document.getElementById('filter-total-count');
                if (totalCountEl && json.data.total_count !== undefined) {
                    totalCountEl.textContent = ` (${json.data.total_count} ${t('items')})`;
                    totalCountEl.classList.remove('hidden');
                }
                if (!append) renderCategoryHeader(json.data.total_count || 0);
            } else if (json.data.pagination) {
                // Fallback for pagination object format
                hasMoreProducts = json.data.pagination.has_next || json.data.pagination.has_more || false;
                console.log('[InfiniteScroll] Pagination:', json.data.pagination, 'hasMoreProducts:', hasMoreProducts);
                
                const totalCountEl = document.getElementById('filter-total-count');
                if (totalCountEl) {
                    const total = json.data.pagination.total_items || json.data.total_count || 0;
                    totalCountEl.textContent = ` (${total} ${t('items')})`;
                    totalCountEl.classList.remove('hidden');
                }
            } else {
                hasMoreProducts = false;
            }
            
            // Update load more indicator
            updateLoadMoreIndicator();
            removeInfiniteScrollLoading();
            
            // Hide old pagination (we use infinite scroll now)
            const paginationContainer = document.getElementById('pagination-container');
            if (paginationContainer) {
                paginationContainer.innerHTML = '';
            }
        } else {
            throw new Error('Invalid response');
        }
    } catch (error) {
        console.error('Failed to fetch products:', error);
        if (!append) {
            grid.innerHTML = `
                <div class="col-span-full text-center py-12">
                    <p class="text-red-500">${escapeHtml(t('Error loading products'))}</p>
                </div>
            `;
        }
    } finally {
        isLoadingMore = false;
        removeInfiniteScrollLoading();
        
        // Remove loading state from filter panels
        const filterPanelEl = document.getElementById('filter-panel');
        const filterSidebarEl = document.getElementById('filterSidebar');
        if (filterPanelEl) filterPanelEl.classList.remove('opacity-50', 'pointer-events-none');
        if (filterSidebarEl) filterSidebarEl.classList.remove('opacity-50', 'pointer-events-none');
    }
}

/**
 * Append products to existing grid
 */
function appendProducts(products: Product[], storesLookup: Record<string, Store> = {}): void {
    const grid = document.getElementById('products-grid');
    removeInfiniteScrollLoading();
    if (!grid || !products || products.length === 0) return;

    // Remove loading indicator if present
    const loadingEl = grid.querySelector('.infinite-scroll-loading');
    if (loadingEl) loadingEl.remove();

    // Process and append products with stores lookup
    const productItems = ProductService.processProductsForDisplay(products, storesLookup);
    const html = productItems.map(item => renderProductCard(item, storesLookup)).join('');
    grid.insertAdjacentHTML('beforeend', html);
}

/**
 * Load more products (for infinite scroll)
 */
async function loadMoreProducts(): Promise<void> {
    if (isLoadingMore || !hasMoreProducts || !currentCategorySlug) return;
    
    isLoadingMore = true;
    currentOffset += ITEMS_PER_PAGE;
    
    // Show loading spinner at bottom of grid
    const grid = document.getElementById('products-grid');
    if (grid) {
        grid.insertAdjacentHTML('beforeend', `
            <div class="infinite-scroll-loading col-span-full flex justify-center py-6">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
        `);
    }
    
    await fetchAndRenderProducts(currentSubcategorySlug || currentCategorySlug || '', currentFilters, true);
}

/**
 * Update loading indicator
 */
function updateLoadMoreIndicator(): void {
    const container = document.getElementById('pagination-container');
    if (!container) return;

    container.innerHTML = '';
}

/**
 * Setup infinite scroll
 */
function setupInfiniteScroll(): void {
    console.log('[InfiniteScroll] Setting up scroll listener');
    
    window.addEventListener('scroll', () => {
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const clientHeight = window.innerHeight;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        // Load more when user is 500px from bottom
        if (distanceFromBottom < 500) {
            if (isLoadingMore) {
                console.log('[InfiniteScroll] Already loading...');
                return;
            }
            if (!hasMoreProducts) {
                console.log('[InfiniteScroll] No more products');
                return;
            }
            console.log('[InfiniteScroll] Triggering load, distance:', distanceFromBottom);
            loadMoreProducts();
        }
    });
}

/**
 * Update pagination UI
 */
function updatePagination(pagination: {
    current_page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
}): void {
    const paginationContainer = document.getElementById('pagination-container');
    if (!paginationContainer || !pagination) return;

    const { current_page, total_pages, has_next, has_previous } = pagination;
    
    if (total_pages <= 1) {
        paginationContainer.innerHTML = '';
        return;
    }

    const prevDisabled = !has_previous ? 'disabled opacity-50 cursor-not-allowed' : '';
    const nextDisabled = !has_next ? 'disabled opacity-50 cursor-not-allowed' : '';

    paginationContainer.innerHTML = `
        <div class="flex items-center justify-center gap-2 mt-8">
            <button 
                type="button"
                class="pagination-btn px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 ${prevDisabled}"
                data-page="${current_page - 1}"
                ${!has_previous ? 'disabled' : ''}
            >
                ${escapeHtml(t('Previous'))}
            </button>
            <span class="text-sm text-slate-600">
                ${escapeHtml(t('Page'))} ${current_page} ${escapeHtml(t('of'))} ${total_pages}
            </span>
            <button 
                type="button"
                class="pagination-btn px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 ${nextDisabled}"
                data-page="${current_page + 1}"
                ${!has_next ? 'disabled' : ''}
            >
                ${escapeHtml(t('Next'))}
            </button>
        </div>
    `;
}

/**
 * Handle subcategory click
 */
async function handleSubcategoryClick(slug: string | null): Promise<void> {
    currentSubcategorySlug = slug;
    
    // Update URL without reload
    const lang = document.documentElement.lang?.split('-')[0] || 'en';
    const newUrl = `/${lang}/grocery/category/${currentCategorySlug}/${slug ? slug + '/' : ''}`;
    window.history.pushState({ categorySlug: currentCategorySlug, subcategorySlug: slug }, '', newUrl);
    
    // Update active state
    if (currentCategory) {
        renderSubcategories(currentCategory.children || [], slug);
    }
    
    // Fetch products for subcategory
    const filters = filterPanel ? filterPanel.getFilters() : {};
    await fetchAndRenderProducts(slug || currentCategorySlug || '', filters);
}

/**
 * Scroll to products section
 */
function scrollToProducts(): void {
    const section = document.getElementById('products-section');
    if (!section) return;
    
    const offset = section.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({
        top: Math.max(0, offset),
        behavior: 'smooth'
    });
}

/**
 * Initialize category detail page
 */
async function initCategoryDetailPage(categorySlug: string, subcategorySlug: string | null = null): Promise<void> {
    currentCategorySlug = categorySlug;
    currentSubcategorySlug = subcategorySlug;

    // Initialize currency toggle for filter panels
    initCurrencyToggle('currency-toggle-container', async (newCode: string) => {
        // Reload products when currency changes
        const filters = filterPanel ? filterPanel.getFilters() : {};
        await fetchAndRenderProducts(currentSubcategorySlug || currentCategorySlug || '', filters);
    });

    // Initialize category pills
    await renderCategoryPills();

    // Initialize filter panels (desktop and mobile)
    filterPanel = new FilterPanel({
        containerId: 'filter-panel',
        onFilterChange: async (filters: FilterParams) => {
            await fetchAndRenderProducts(currentSubcategorySlug || currentCategorySlug || '', filters);
        }
    });
    filterPanel.init();
    filterPanel.bindSearchInput('filter-search-top');
    
    // Initialize mobile filter panel
    mobileFilterPanel = new FilterPanel({
        containerId: 'filterSidebar',
        suffix: '-mobile',
        onFilterChange: async (filters: FilterParams) => {
            await fetchAndRenderProducts(currentSubcategorySlug || currentCategorySlug || '', filters);
        }
    });
    mobileFilterPanel.init();

    // Load category data
    await loadCategory(categorySlug, subcategorySlug);

    // Setup event listeners
    setupEventListeners();
    
    // Setup load more button
    setupInfiniteScroll();
}

/**
 * Render top-level category pills
 */
async function renderCategoryPills(): Promise<void> {
    const railContainer = document.getElementById('category-pill-rail');
    if (!railContainer) return;

    try {
        // Show loading skeletons
        const loader = new SkeletonLoader();
        railContainer.innerHTML = loader.renderCategoryPillSkeleton(12);

        const categoryService = new CategoryService();
        const categories = await categoryService.fetchCategories();
        
        if (!categories || categories.length === 0) {
            railContainer.innerHTML = '';
            return;
        }

        const currentLang = document.documentElement.lang?.split('-')[0] || 'en';
        
        railContainer.innerHTML = categories.map(cat => {
            const isActive = cat.slug === currentCategorySlug;
            
            return `
                <button type="button"
                   class="category-pill ${isActive ? 'is-active' : ''}"
                   data-slug="${cat.slug}"
                   data-url="/${currentLang}/grocery/category/${cat.slug}/"
                   ${isActive ? 'aria-pressed="true"' : 'aria-pressed="false"'}>
                    ${escapeHtml((cat as any).translated_name || cat.name)}
                </button>
            `;
        }).join('');

        // Initialize scrollable rail
        const rail = new ScrollableRail('category-pill-rail');
        rail.init();
        
    } catch (error) {
        console.error('Failed to render category pills:', error);
    }
}

/**
 * Load category data
 */
async function loadCategory(categorySlug: string, subcategorySlug: string | null = null): Promise<void> {
    try {
        // Show subcategory skeletons
        const subGrid = document.getElementById('subcategories-grid');
        if (subGrid) {
            const loader = new SkeletonLoader();
            subGrid.innerHTML = loader.renderCategoryPillSkeleton(6);
            subGrid.closest('.subcategories-section')?.classList.remove('hidden');
        }

        const categoryService = new CategoryService();
        currentCategory = await categoryService.fetchCategory(categorySlug);
        
        if (currentCategory) {
            renderCategoryHeader();

            // Render subcategories
            renderSubcategories(currentCategory.children || [], subcategorySlug);
            
            // Fetch products
            const filters = filterPanel ? filterPanel.getFilters() : {};
            await fetchAndRenderProducts(subcategorySlug || categorySlug, filters);
        }
    } catch (error) {
        console.error('Failed to load category:', error);
    }
}

/**
 * Setup event listeners
 */
function setupEventListeners(): void {
    // Mobile filter toggle
    const filterToggle = document.getElementById('filterToggle');
    const filterSidebar = document.getElementById('filterSidebar');
    
    if (filterToggle && filterSidebar) {
        filterToggle.addEventListener('click', () => {
            const isHidden = filterSidebar.classList.contains('hidden');
            const svg = filterToggle.querySelector('svg');
            const textSpan = filterToggle.querySelector('span:last-child');
            
            if (isHidden) {
                filterSidebar.classList.remove('hidden');
                svg?.classList.add('rotate-180');
                if (textSpan && textSpan.childNodes[0] instanceof Text) {
                    textSpan.childNodes[0].textContent = t('Hide');
                }
            } else {
                filterSidebar.classList.add('hidden');
                svg?.classList.remove('rotate-180');
                if (textSpan && textSpan.childNodes[0] instanceof Text) {
                    textSpan.childNodes[0].textContent = t('Show');
                }
            }
        });
    }

    // Subcategory clicks
    document.addEventListener('click', (e) => {
        const chip = (e.target as HTMLElement).closest('.subcategory-chip');
        if (chip && chip instanceof HTMLElement) {
            e.preventDefault();
            const slug = chip.dataset.slug || null;
            if (slug) {
                handleSubcategoryClick(slug);
            }
        }
    });

    // Category pill clicks (partial load instead of full page reload)
    document.addEventListener('click', (e) => {
        const pill = (e.target as HTMLElement).closest('.category-pill');
        if (pill && pill instanceof HTMLElement) {
            e.preventDefault();
            const slug = pill.dataset.slug;
            const url = pill.dataset.url;
            
            if (!slug || !url) return;
            
            // Skip if already on this category
            if (slug === currentCategorySlug) return;
            
            // Update state and URL
            currentCategorySlug = slug;
            currentSubcategorySlug = null;
            
            // Update URL without reload
            window.history.pushState({ categorySlug: slug }, '', url);
            
            // Reload the category data
            loadCategory(slug, null);
            
            // Update category pills active state
            document.querySelectorAll('.category-pill').forEach(p => {
                const isActive = (p as HTMLElement).dataset.slug === slug;
                p.classList.toggle('is-active', isActive);
                p.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        }
    });

    // Handle browser back/forward
    window.addEventListener('popstate', (e: PopStateEvent) => {
        if (e.state && typeof e.state === 'object' && 'categorySlug' in e.state) {
            const state = e.state as { categorySlug?: string; subcategorySlug?: string | null };
            if (state.categorySlug) {
                currentCategorySlug = state.categorySlug;
                currentSubcategorySlug = state.subcategorySlug || null;
                loadCategory(currentCategorySlug, currentSubcategorySlug);
            }
        }
    });
}

/**
 * Get category slug from URL
 */
function getCategorySlugsFromUrl(): { categorySlug: string | null; subcategorySlug: string | null } {
    const pathname = window.location.pathname;
    // URL pattern: /{lang}/grocery/category/{category_slug}/{subcategory_slug}/
    const match = pathname.match(/\/grocery\/category\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    
    if (match) {
        return {
            categorySlug: match[1],
            subcategorySlug: match[2] || null
        };
    }
    
    return { categorySlug: null, subcategorySlug: null };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        const { categorySlug, subcategorySlug } = getCategorySlugsFromUrl();
        if (categorySlug) {
            initCategoryDetailPage(categorySlug, subcategorySlug);
        }
    });
} else {
    const { categorySlug, subcategorySlug } = getCategorySlugsFromUrl();
    if (categorySlug) {
        initCategoryDetailPage(categorySlug, subcategorySlug);
    }
}

// Export for testing
export { initCategoryDetailPage, renderSubcategories, renderProducts, handleSubcategoryClick };
